import type { ForecastContextSelectedOptions } from "@appretio/context-selector";
import { useRetainedState } from "@appretio/shared";
import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from "react";
import { FORECAST_CONTEXT_DIMENSIONS } from "../../forecastContext";
import { getVersionOptions } from "../../utils/summary/summaryPageHelpers";
import { type CubePage, pageScope } from "../useCubeSelectionState";
import { useFetchCubeYears } from "../useFetchCubeYears";
import { useFetchVersions } from "../useFetchVersions";
import { useOpenOnMainCube } from "../useOpenOnMainCube";
import { useScopedBudgetVersions } from "../useScopedBudgetVersions";

/** Options for the summary source selection sub-hook. */
interface UseSummarySourceSelectionOptions {
  /** The page whose source selection this is. */
  page: CubePage;
  /** Currently selected context filter values. */
  selectedContextOptions: ForecastContextSelectedOptions;
}

/** Inputs for {@link useVersionYearSync} — one version/year selection pair. */
interface VersionYearSyncOptions {
  /** Whether the versions fetch backing ``options`` has resolved. */
  optionsLoaded: boolean;
  /** Version options currently offered for the producer. */
  options: { label: string; value: string }[];
  /** Currently selected cube version id ("" when unselected). */
  version: string;
  /** Setter for the version selection. */
  setVersion: Dispatch<SetStateAction<string>>;
  /** Years available on the cube behind this pair (undefined while loading). */
  years: string[] | undefined;
  /** Preferred default year; used only when ``years`` contains it. */
  currentFinancialYear: string | null | undefined;
  /** Currently selected financial year ("" when unselected). */
  year: string;
  /** Setter for the year selection. */
  setYear: Dispatch<SetStateAction<string>>;
}

/**
 * Keeps one version/year selection pair consistent with the offered
 * options: auto-selects when exactly one version exists, fills the
 * default year (current fiscal year when the cube covers it, else the
 * cube's latest year), and — critically for context switches — clears a
 * previously chosen version/year that the refreshed option lists no
 * longer offer. Without the clearing, a stale cube id from the old
 * context (budget versions are brand-scoped) would silently ride along
 * on the next Apply — e.g. an index computed against the old brand.
 */
function useVersionYearSync({
  optionsLoaded,
  options,
  version,
  setVersion,
  years,
  currentFinancialYear,
  year,
  setYear
}: VersionYearSyncOptions) {
  useEffect(() => {
    if (options.length === 1 && version !== options[0].value) {
      setVersion(options[0].value);
    }
  }, [options, version, setVersion]);

  // Guarded on ``optionsLoaded`` so an in-flight refetch (options
  // momentarily empty) doesn't wipe a still-valid selection.
  useEffect(() => {
    if (!version || !optionsLoaded) return;
    if (!options.some((o) => o.value === version)) {
      setVersion("");
      setYear("");
    }
  }, [version, optionsLoaded, options, setVersion, setYear]);

  // One effect: a separate default and stale-check would fight over a
  // year outside ``years``.
  useEffect(() => {
    if (!version) {
      // The Version select is clearable, so it can drop the version alone.
      if (year) setYear("");
      return;
    }
    if (!years) return;
    if (year && years.includes(year)) return;
    const fallback =
      currentFinancialYear && years.includes(currentFinancialYear)
        ? currentFinancialYear
        : (years.at(-1) ?? "");
    if (year !== fallback) setYear(fallback);
  }, [version, year, years, currentFinancialYear, setYear]);
}

/** The years a cube covers: the raw fetch for the sync, select options for the bar. */
function useCubeYears(version: string) {
  const { data } = useFetchCubeYears(version || undefined);
  const options = useMemo(
    () => (data?.years ?? []).map((y) => ({ label: y, value: y })),
    [data?.years]
  );
  return { data, options };
}

/**
 * Whether the catalogue offers ``version`` under ``producer``; undefined
 * until it has landed. Budget versions are scoped to the brand selected now,
 * not the one applied, so a budget cube never reads as gone.
 */
function versionOffered(
  versionsData: ReturnType<typeof useFetchVersions>["data"],
  producer: string,
  version: string
): boolean | undefined {
  if (producer === "budget") return true;
  if (!versionsData) return undefined;
  return getVersionOptions(producer, versionsData).some(
    (option) => option.value === version
  );
}

/** Whether a budget cube is frozen; undefined when the list does not offer it. */
export function isBudgetCubeFrozen(
  budgetVersionsData: ReturnType<typeof useScopedBudgetVersions>["data"],
  cubeId: string
): boolean | undefined {
  return budgetVersionsData?.budget_versions?.find(
    (budgetVersion) => budgetVersion.cube_id === cubeId
  )?.is_frozen;
}

/** One of the page's retained selections. */
function useRetained(page: CubePage, key: string, initialValue: string) {
  return useRetainedState(pageScope(page), key, initialValue);
}

/**
 * The forecast summary opens on User, the main cube and — through
 * ``useVersionYearSync`` — the current financial year; budget opens blank.
 */
function useOpenSummaryOnMainCube(
  page: CubePage,
  versionsData: ReturnType<typeof useFetchVersions>["data"],
  setProducer: Dispatch<SetStateAction<string>>,
  setVersion: Dispatch<SetStateAction<string>>
) {
  const selectMainCube = useCallback(
    (cubeId: string) => {
      setProducer("user");
      setVersion(cubeId);
    },
    [setProducer, setVersion]
  );
  useOpenOnMainCube(
    page,
    page === "forecast-summary",
    versionsData,
    selectMainCube
  );
}

/**
 * Manages producer/version/year selection for both main and index sources,
 * dimension selectors, version fetching, auto-select effects, and frozen
 * state. Selections are retained per page, so each summary page comes back
 * to what it had.
 */
export function useSummarySourceSelection({
  page,
  selectedContextOptions
}: UseSummarySourceSelectionOptions) {
  const [producer, setProducer] = useRetained(page, "producer", "");
  const [version, setVersion] = useRetained(page, "version", "");
  const [year, setYear] = useRetained(page, "year", "");

  const [idxProducer, setIdxProducer] = useRetained(page, "idxProducer", "");
  const [idxVersion, setIdxVersion] = useRetained(page, "idxVersion", "");
  const [idxYear, setIdxYear] = useRetained(page, "idxYear", "");

  const [tableDim, setTableDim] = useRetained(
    page,
    "tableDim",
    "collection_type"
  );
  const [rowDim, setRowDim] = useRetained(page, "rowDim", "brand");

  // --- Data hooks ---
  const { data: versionsData } = useFetchVersions();
  useOpenSummaryOnMainCube(page, versionsData, setProducer, setVersion);
  const selectedBrand = selectedContextOptions?.brand?.[0] ?? "";
  const { data: budgetVersionsData } = useScopedBudgetVersions(selectedBrand);

  // --- Version options ---
  const mainVersionOptions = useMemo(
    () => getVersionOptions(producer, versionsData, budgetVersionsData),
    [versionsData, budgetVersionsData, producer]
  );
  const idxVersionOptions = useMemo(
    () => getVersionOptions(idxProducer, versionsData, budgetVersionsData),
    [versionsData, budgetVersionsData, idxProducer]
  );

  // --- Frozen state ---
  const isFrozen =
    producer === "budget" &&
    (isBudgetCubeFrozen(budgetVersionsData, version) ?? false);

  // --- Year options ---
  const { data: cubeYearsData, options: yearOptions } = useCubeYears(version);
  const { data: idxCubeYearsData, options: idxYearOptions } =
    useCubeYears(idxVersion);

  // --- Auto-select + stale-selection sync (see useVersionYearSync) ---
  useVersionYearSync({
    optionsLoaded:
      producer === "budget" ? !!budgetVersionsData : !!versionsData,
    options: mainVersionOptions,
    version,
    setVersion,
    years: cubeYearsData?.years,
    currentFinancialYear: cubeYearsData?.current_financial_year,
    year,
    setYear
  });
  useVersionYearSync({
    optionsLoaded:
      idxProducer === "budget" ? !!budgetVersionsData : !!versionsData,
    options: idxVersionOptions,
    version: idxVersion,
    setVersion: setIdxVersion,
    years: idxCubeYearsData?.years,
    currentFinancialYear: idxCubeYearsData?.current_financial_year,
    year: idxYear,
    setYear: setIdxYear
  });

  const isVersionOffered = useCallback(
    (offeredProducer: string, offeredVersion: string) =>
      versionOffered(versionsData, offeredProducer, offeredVersion),
    [versionsData]
  );

  const cubeId = version || undefined;

  // --- Dimension filtering ---
  const tableDimOptions = FORECAST_CONTEXT_DIMENSIONS.filter(
    (o) => o.value !== rowDim
  );
  const rowDimOptions = FORECAST_CONTEXT_DIMENSIONS.filter(
    (o) => o.value !== tableDim
  );

  return {
    producer,
    setProducer,
    version,
    setVersion,
    year,
    setYear,
    mainVersionOptions,
    yearOptions,
    idxProducer,
    setIdxProducer,
    idxVersion,
    setIdxVersion,
    idxYear,
    setIdxYear,
    idxVersionOptions,
    idxYearOptions,
    tableDim,
    setTableDim,
    rowDim,
    setRowDim,
    tableDimOptions,
    rowDimOptions,
    isFrozen,
    budgetVersionsData,
    // The producer this page writes to.
    saveProducer: page === "budget-summary" ? "budget" : "user",
    cubeId,
    isVersionOffered
  };
}
